<template>
  <div class="page">
    <div class="card">
      <div class="head">
        <div>
          <h2>Change Password</h2>
          <p class="sub">Update your password securely.</p>
        </div>
      </div>

      <el-alert
        v-if="!user"
        title="You must be signed in to change password."
        type="warning"
        show-icon
        class="mb"
      />

      <el-skeleton v-else-if="loading" :rows="4" animated />

      <template v-else>
        <el-alert
          v-if="mode === 'link'"
          title="You signed in with Google. Create a password to enable Email/Password sign in too."
          type="warning"
          show-icon
          class="mb"
        />

        <el-alert
          v-else
          title="For security, enter your current password to change it."
          type="info"
          show-icon
          class="mb"
        />

        <div class="content">
          <!-- LEFT: form -->
          <el-form
            ref="formRef"
            :model="form"
            :rules="rules"
            label-position="top"
            class="form"
          >
            <el-form-item v-if="mode === 'change'" label="Current Password" prop="currentPassword">
              <el-input
                v-model="form.currentPassword"
                type="password"
                show-password
                autocomplete="current-password"
                placeholder="Enter current password"
              />
            </el-form-item>

            <el-form-item :label="mode === 'link' ? 'Create New Password' : 'New Password'" prop="newPassword">
              <el-input
                v-model="form.newPassword"
                type="password"
                show-password
                autocomplete="new-password"
                placeholder="Enter new password"
              />
            </el-form-item>

            <el-form-item label="Confirm New Password" prop="confirmPassword">
              <el-input
                v-model="form.confirmPassword"
                type="password"
                show-password
                autocomplete="new-password"
                placeholder="Re-enter new password"
              />
            </el-form-item>

            <div class="actions">
              <el-button type="primary" :loading="saving" @click="onSubmit">
                {{ mode === 'link' ? 'Create Password' : 'Update Password' }}
              </el-button>

              <el-button
                v-if="mode === 'link'"
                :disabled="saving"
                @click="showGoogleHelp = !showGoogleHelp"
              >
                Why can’t I change Google password here?
              </el-button>
            </div>

            <el-collapse v-if="mode === 'link' && showGoogleHelp" class="mt">
              <el-collapse-item title="Google sign-in passwords are managed by Google">
                <div class="help">
                  Since you signed in with Google, your password is managed in your Google account.
                  Creating a password here links Email/Password to your account, so you can sign in either way.
                </div>
              </el-collapse-item>
            </el-collapse>
          </el-form>

          <!-- RIGHT: password rules with live checks -->
          <div class="rules">
            <div class="rules-title">Password rules</div>

            <div class="rule-row">
              <el-icon class="rule-icon" :class="ruleChecks.len ? 'ok' : 'bad'" size="large">
                <CircleCheckFilled v-if="ruleChecks.len" />
                <CircleCloseFilled v-else />
              </el-icon>
              <span class="rule-text">Minimum 8 characters</span>
            </div>

            <div class="rule-row">
              <el-icon class="rule-icon" :class="ruleChecks.lower ? 'ok' : 'bad'" size="large">
                <CircleCheckFilled v-if="ruleChecks.lower" />
                <CircleCloseFilled v-else />
              </el-icon>
              <span class="rule-text">At least 1 lowercase letter</span>
            </div>

            <div class="rule-row">
              <el-icon class="rule-icon" :class="ruleChecks.upper ? 'ok' : 'bad'" size="large">
                <CircleCheckFilled v-if="ruleChecks.upper" />
                <CircleCloseFilled v-else />
              </el-icon>
              <span class="rule-text">At least 1 uppercase letter</span>
            </div>

            <div class="rule-row">
              <el-icon class="rule-icon" :class="ruleChecks.number ? 'ok' : 'bad'" size="large">
                <CircleCheckFilled v-if="ruleChecks.number" />
                <CircleCloseFilled v-else />
              </el-icon>
              <span class="rule-text">At least 1 number</span>
            </div>

            <div class="rule-row">
              <el-icon class="rule-icon" :class="ruleChecks.special ? 'ok' : 'bad'" size="large">
                <CircleCheckFilled v-if="ruleChecks.special" />
                <CircleCloseFilled v-else />
              </el-icon>
              <span class="rule-text">At least 1 special character</span>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { auth } from '@/firebase'
import {
  EmailAuthProvider,
  onAuthStateChanged,
  reauthenticateWithCredential,
  updatePassword,
  linkWithCredential,
} from 'firebase/auth'
import { ElMessage } from 'element-plus'
import { CircleCheckFilled, CircleCloseFilled } from '@element-plus/icons-vue'

export default {
  name: 'ChangePassword',
  components: { CircleCheckFilled, CircleCloseFilled },
  data() {
    const validateConfirm = (rule, value, callback) => {
      if (!value) return callback(new Error('Please confirm your new password'))
      if (value !== this.form.newPassword) return callback(new Error('Passwords do not match'))
      callback()
    }

    const validateStrongPassword = (rule, value, callback) => {
      const v = String(value || '')
      const ok =
        v.length >= 8 &&
        /[a-z]/.test(v) &&
        /[A-Z]/.test(v) &&
        /[0-9]/.test(v) &&
        /[^A-Za-z0-9]/.test(v)

      if (!ok) return callback(new Error('Password does not meet the required rules'))
      callback()
    }

    return {
      user: null,
      loading: true,
      saving: false,
      showGoogleHelp: false,
      mode: 'change',

      form: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      },

      rules: {
        currentPassword: [
          { required: true, message: 'Current password is required', trigger: 'blur' },
        ],
        newPassword: [
          { required: true, message: 'New password is required', trigger: 'blur' },
          { validator: validateStrongPassword, trigger: ['blur', 'change'] },
        ],

        confirmPassword: [
          { validator: validateConfirm, trigger: ['blur', 'change'] },
        ],
      },
    }
  },
  computed: {
    ruleChecks() {
      const v = this.form.newPassword || ''
      return {
        len: v.length >= 8,
        lower: /[a-z]/.test(v),
        upper: /[A-Z]/.test(v),
        number: /[0-9]/.test(v),
        special: /[^A-Za-z0-9]/.test(v),
      }
    },
  },
  mounted() {
    this._unsub = onAuthStateChanged(auth, (u) => {
      this.user = u || null
      this.loading = false
      this.resolveMode()
      this.resetForm()
    })
  },
  beforeUnmount() {
    this._unsub && this._unsub()
  },
  methods: {
    resolveMode() {
      const u = this.user
      if (!u) return

      const providers = (u.providerData || []).map(p => p.providerId)
      const hasPassword = providers.includes('password')
      const hasGoogle = providers.includes('google.com')

      this.mode = (!hasPassword && hasGoogle) ? 'link' : 'change'
    },
    resetForm() {
      this.form.currentPassword = ''
      this.form.newPassword = ''
      this.form.confirmPassword = ''
      this.showGoogleHelp = false
      if (this.$refs.formRef) this.$refs.formRef.clearValidate()
    },

    async onSubmit() {
      if (!this.user) return
      const formRef = this.$refs.formRef
      if (!formRef) return

      try {
        await formRef.validate()
        this.saving = true

        if (this.mode === 'change') {
          await this.changePasswordFlow()
          ElMessage.success({ message: 'Password updated successfully.', showClose: true, duration: 3500 })
        } else {
          await this.linkPasswordFlow()
          ElMessage.success({ message: 'Password created and linked. You can now sign in with email and password too.', showClose: true, duration: 4500 })
          this.resolveMode()
        }

        this.resetForm()
      } catch (e) {
        const msg = this.humanError(e)
        if (msg) ElMessage.error({ message: msg, showClose: true, duration: 4500 })
      } finally {
        this.saving = false
      }
    },

    async changePasswordFlow() {
      const u = auth.currentUser
      if (!u) throw new Error('Not signed in')
      const email = u.email
      if (!email) throw new Error('No email found for this account')

      const cred = EmailAuthProvider.credential(email, this.form.currentPassword)
      await reauthenticateWithCredential(u, cred)
      await updatePassword(u, this.form.newPassword)
    },

    async linkPasswordFlow() {
      const u = auth.currentUser
      if (!u) throw new Error('Not signed in')
      const email = u.email
      if (!email) throw new Error('No email found for this account')

      const cred = EmailAuthProvider.credential(email, this.form.newPassword)
      await linkWithCredential(u, cred)
    },

    humanError(e) {
      if (e === false) return null

      const code = e?.code || ''
      const msg = e?.message || ''

      if (code.includes('auth/wrong-password')) return 'Current password is incorrect.'
      if (code.includes('auth/invalid-credential')) return 'Current password is incorrect.'
      if (code.includes('auth/too-many-requests')) return 'Too many attempts. Try again later.'
      if (code.includes('auth/requires-recent-login')) return 'Please log out and log back in, then try again.'
      if (code.includes('auth/weak-password')) return 'Password is too weak. Meet all rules on the right.'
      if (code.includes('auth/provider-already-linked')) return 'Password login is already enabled for this account.'
      if (code.includes('auth/email-already-in-use')) return 'This email is already linked to another account.'
      if (code.includes('auth/credential-already-in-use')) return 'These credentials are already linked to another account.'

      if (msg.includes('Password does not meet')) return 'Password does not meet the required rules.'

      return msg || 'Unable to update password.'
    },
  },
}
</script>

<style scoped>
.page {
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 18px 10px;
}

.card {
  width: min(980px, 100%);
  background: #ffffff;
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 22px;
  box-shadow: 0 10px 26px rgba(0,56,49,.08);
}

.head {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 14px;
}

h2 {
  margin: 0;
  color: #003831;
  font-weight: 900;
}

.sub {
  margin: 0;
  color: var(--muted);
  font-weight: 600;
}

.mb { margin-bottom: 12px; overflow: hidden; }
.mt { margin-top: 12px; }

.content {
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 18px;
  align-items: start;
}

.form {
  width: 100%;
}

.rules {
  border: 0.01 rem black solid;
  border-radius: 14px;
  padding: 14px 14px 12px;
  background: white;
  box-shadow: 0 10px 22px rgba(0,56,49,0.06);
}

.rules-title {
  font-weight: 900;
  color: #003831;
  margin-bottom: 10px;
}

.rule-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 2px;
}

.rule-icon.ok { color: green;}
.rule-icon.bad { color: red }

.rule-text {
  color: rgba(0,0,0,0.72);
  font-weight: 650;
}

.rules-note {
  margin-top: 10px;
  color: var(--muted);
  font-size: 0.9rem;
}

.actions {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-top: 8px;
  flex-wrap: wrap;
}

.help {
  color: var(--muted);
  line-height: 1.5;
}

@media (max-width: 900px) {
  .content {
    grid-template-columns: 1fr;
  }
}
</style>
